/**
 * TopBar — exibe um slot pra ações no canto esquerdo (ex: botão hamburger
 * em mobile) e o user menu no canto direito.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { BellRing, LogOut, ShieldCheck, User as UserIcon, ChevronDown } from "lucide-react";
import clsx from "clsx";

import { useAuth } from "@/contexts/AuthContext";
import type { Role } from "@/types";
import { AlertBell } from "./AlertBell";
import { ThemeToggle } from "./ThemeToggle";

const ROLE_LABEL: Record<Role, string> = {
  super_admin: "Super Admin",
  admin: "Admin",
  operator: "Operador",
  viewer: "Viewer",
};

const ROLE_COLOR: Record<Role, string> = {
  super_admin: "bg-amber-400/15  text-amber-300  border border-amber-400/20",
  admin:       "bg-cyan-400/15   text-cyan-300   border border-cyan-400/20",
  operator:    "bg-emerald-400/15 text-emerald-300 border border-emerald-400/20",
  viewer:      "bg-slate-400/15  text-slate-300  border border-slate-400/20",
};

export function TopBar({ left }: { left?: ReactNode }) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const user = auth.user;
  if (!user) return null;

  const initials = (user.name ?? user.email)
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");

  return (
    <header
      /*
       * PWA no iOS: com `viewport-fit=cover` + status bar translúcida, o topo
       * da página fica DEBAIXO do relógio, da bateria e da Dynamic Island. O
       * padding vem do inset do próprio aparelho — 0 em telas sem entalhe,
       * então não custa nada no desktop.
       *
       * A altura é h-16 MAIS o inset (não h-16 total), senão a faixa segura
       * comeria a altura útil da barra e os ícones ficariam espremidos.
       */
      className="flex min-h-[calc(4rem+env(safe-area-inset-top))] items-center justify-between gap-3 border-b border-white/10 bg-[#0a0f1a] px-4 pt-[env(safe-area-inset-top)] text-slate-200 md:px-6"
    >
      <div className="flex items-center gap-3">{left}</div>

      <div className="flex items-center gap-2 md:gap-3">
        <ThemeToggle />
        <AlertBell />

        <span
          className={clsx(
            "hidden rounded-full px-2 py-0.5 text-xs font-medium md:inline",
            ROLE_COLOR[user.role],
          )}
        >
          {user.role === "super_admin" && (
            <ShieldCheck className="mr-1 inline h-3 w-3 align-text-top" />
          )}
          {ROLE_LABEL[user.role]}
        </span>

        <div ref={ref} className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-white/5"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-400/15 text-sm font-semibold text-cyan-300 border border-cyan-400/25">
              {initials || <UserIcon className="h-4 w-4" />}
            </span>
            <span className="hidden text-left md:flex md:flex-col md:leading-tight">
              <span className="font-medium text-slate-100">
                {user.name ?? user.email.split("@")[0]}
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">{user.email}</span>
            </span>
            <ChevronDown className="hidden h-4 w-4 text-slate-400 dark:text-slate-500 md:block" />
          </button>

          {open && (
            <div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-lg border border-white/10 bg-[#0a0f1a] shadow-card-hover animate-fade-in">
              <div className="border-b border-white/10 p-3">
                <div className="text-sm font-semibold text-slate-100">
                  {user.name ?? user.email.split("@")[0]}
                </div>
                <div className="truncate text-xs text-slate-400 dark:text-slate-500">{user.email}</div>
              </div>
              <Link
                to="/notificacoes"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-slate-100"
                onClick={() => setOpen(false)}
              >
                <BellRing className="h-4 w-4" />
                Notificações
              </Link>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-slate-100"
                onClick={() => {
                  setOpen(false);
                  auth.logout();
                }}
              >
                <LogOut className="h-4 w-4" />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
